import type { EventBootstrapResponse } from '@irace/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchBootstrap } from '../api/client.ts';
import { applyBranding } from '../lib/branding.ts';
import { BootstrapLoading, BootstrapOffline } from './BootstrapLoading.tsx';
import { CheckinExperience } from './CheckinExperience.tsx';
import { EventUnavailable, type UnavailableReason } from './EventUnavailable.tsx';
import { resolveLocationIntent } from './locationIntent.ts';

type PageState =
  | { kind: 'loading' }
  | { kind: 'offline' }
  | { kind: 'unavailable'; reason: UnavailableReason }
  | { kind: 'ready'; bootstrap: EventBootstrapResponse };

export function CheckinPage({
  slug,
  locationCode,
}: {
  slug: string;
  /** Second path segment, present when the QR was printed for one venue. */
  locationCode: string | null;
}) {
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  // Stable across renders, so the memo the form keeps on top of it survives a
  // keystroke. `window.location.search` cannot change without a reload here.
  const locations = state.kind === 'ready' ? state.bootstrap.locations : null;
  const intent = useMemo(
    () => resolveLocationIntent(locations ?? [], locationCode, window.location.search),
    [locations, locationCode],
  );

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      const result = await fetchBootstrap(slug, controller.signal);
      if (controller.signal.aborted) return;

      if (result.kind === 'not_found') {
        setState({ kind: 'unavailable', reason: 'not_found' });
        return;
      }
      if (result.kind !== 'ok') {
        setState({ kind: 'offline' });
        return;
      }

      applyBranding(result.bootstrap.event);
      setState({ kind: 'ready', bootstrap: result.bootstrap });
    })();

    return () => {
      controller.abort();
    };
  }, [slug, attempt]);

  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setAttempt((current) => current + 1);
  }, []);

  if (state.kind === 'loading') return <BootstrapLoading />;
  if (state.kind === 'offline') return <BootstrapOffline onRetry={retry} />;
  if (state.kind === 'unavailable') return <EventUnavailable reason={state.reason} />;

  const { bootstrap } = state;
  const { status } = bootstrap.event;

  if (status === 'scheduled' || status === 'closed') {
    return (
      <EventUnavailable
        reason={status}
        event={bootstrap.event}
        organization={bootstrap.organization}
      />
    );
  }

  return <CheckinExperience bootstrap={bootstrap} intent={intent} />;
}
